import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Trash2, X, User, Mail } from 'lucide-react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from '../../config/firebase';
import { useAdminAuth } from '../context/AdminAuthContext';
import AdminLayout from '../components/AdminLayout';

const auth = getAuth(app);

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  color: var(--text-dark);
  font-weight: 700;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary-blue);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  transition: var(--transition);

  &:hover {
    background: #004d8f;
  }
`;

const InfoBox = styled.div`
  background: #E0F2FE;
  border: 1px solid #7DD3FC;
  border-radius: var(--radius-md);
  padding: 1rem;
  margin-bottom: 2rem;
  color: #0369A1;
  font-size: 0.95rem;
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  background: var(--off-white);
  font-weight: 600;
  color: var(--text-dark);
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid var(--border-light);
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  border-radius: var(--radius-md);
  transition: var(--transition);

  &:hover {
    background: #FEE2E2;
    color: #DC2626;
  }
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--radius-lg);
  padding: 2rem;
  width: 100%;
  max-width: 500px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-dark);
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SubmitButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: var(--primary-blue);
  color: white;
  border-radius: var(--radius-md);
  font-weight: 600;

  &:hover {
    background: #004d8f;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: var(--background-light);
  color: var(--text-dark);
  border-radius: var(--radius-md);
  font-weight: 600;

  &:hover {
    background: var(--border-light);
  }
`;

const CloseButton = styled.button`
  background: none;
  color: var(--text-light);
  
  &:hover {
    color: var(--text-dark);
  }
`;

const ErrorMessage = styled.div`
  background: #FEE2E2;
  color: #DC2626;
  padding: 0.75rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
`;

const AdminUsers = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { createUser } = useAdminAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await createUser(formData.email, formData.password);
    
    if (result.success) {
      closeModal();
      alert('Utilisateur créé avec succès !');
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ email: '', password: '' });
    setError('');
  };

  return (
    <AdminLayout title="Utilisateurs">
      <Container>
        <Header>
          <Title>Gestion des utilisateurs</Title>
          <AddButton onClick={() => setIsModalOpen(true)}>
            <Plus size={20} />
            Ajouter un utilisateur
          </AddButton>
        </Header>

        <InfoBox>
          <strong>Information :</strong> Les utilisateurs administrateurs sont gérés via Firebase Authentication. 
          Créez un nouvel utilisateur pour lui donner accès au panneau d'administration.
        </InfoBox>

        <Table>
          <thead>
            <tr>
              <Th>Utilisateur</Th>
              <Th>Email</Th>
              <Th>Statut</Th>
            </tr>
          </thead>
          <tbody>
            {currentUser && (
              <tr>
                <Td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <User size={16} />
                    Connecté
                  </div>
                </Td>
                <Td>{currentUser.email}</Td>
                <Td>
                  <span style={{ 
                    background: '#D1FAE5', 
                    color: '#059669',
                    padding: '0.35rem 0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.85rem',
                    fontWeight: 600
                  }}>
                    Actif
                  </span>
                </Td>
              </tr>
            )}
          </tbody>
        </Table>

        {isModalOpen && (
          <Modal onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>Nouvel utilisateur admin</h2>
                <CloseButton onClick={closeModal}>
                  <X size={24} />
                </CloseButton>
              </ModalHeader>

              <Form onSubmit={handleSubmit}>
                {error && <ErrorMessage>{error}</ErrorMessage>}

                <FormGroup>
                  <Label>Adresse email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="admin@example.com"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Mot de passe</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Min. 6 caractères"
                    minLength="6"
                    required
                  />
                </FormGroup>

                <ButtonGroup>
                  <CancelButton type="button" onClick={closeModal}>
                    Annuler
                  </CancelButton>
                  <SubmitButton type="submit" disabled={isLoading}>
                    {isLoading ? 'Création...' : 'Créer'}
                  </SubmitButton>
                </ButtonGroup>
              </Form>
            </ModalContent>
          </Modal>
        )}
      </Container>
    </AdminLayout>
  );
};

export default AdminUsers;
